// Pricing migration 32 (ADO-3985), T11: what a product card and a list row say for a multi-buy
// deal, as pure functions, and which deal a card shows when a product is in several.
//
// - The text is built from the deal's lowest tier (AC2 amended: "Buy 4, get 1 free"); the feed's
//   description is the title only on the product page and in the dialog. A deal the server marks
//   `DESCRIPTION_ONLY` (no tier gives free units, SL1.0 parity) says its description instead,
//   with no tier and no progress; this file never works that case out from the tiers.
// - The dates are the ones the server sends to show, `displayFrom` and `displayTo`: the deal's own
//   on standard and central billing entries; on WOW no start and the WOW window's end (TQ-4, SL1.0
//   parity), so both strips read `Ends {d Mmm}` there. They are read with the shared day-and-month
//   formatter, never a copy of it. The confirmed date rule: end-only places say
//   `Ends {d Mmm}` when there is an end date; range places say `{d Mmm} – {d Mmm}` with both,
//   `Ends {d Mmm}` with only the end, and nothing otherwise. No year, even across a year end.
// - No "Free: …" segment (TQ-3) and no "+N more" (DQ-4). Which deal a card shows for a product in
//   several is an interim order (`cardDeal`) until the designer sets the rule.
//
// The Figma copy is used as drawn (frames 01, 02, A and B), middle dots and en dash included.

import type {
  FreeUnitsMultibuyDeal,
  MultibuyDeal,
  MultibuyDealTier,
} from '../api/multibuy-deals.client';
import { formatDayMonth } from '../shared/dayMonth';

/** The deal's lowest tier: the smallest buy quantity, then the smallest free quantity. Every
 *  `FREE_UNITS` deal the client keeps has at least one tier. */
export function lowestTier(deal: FreeUnitsMultibuyDeal): MultibuyDealTier {
  return [...deal.tiers].sort(
    (left, right) => left.buyQuantity - right.buyQuantity || left.freeQuantity - right.freeQuantity,
  )[0] as MultibuyDealTier;
}

/** `Buy 4, get 1 free`. */
export function tierText(tier: MultibuyDealTier): string {
  return `Buy ${tier.buyQuantity}, get ${tier.freeQuantity} free`;
}

function dayMonthOf(isoDate: string | undefined): string | null {
  return isoDate === undefined ? null : formatDayMonth(isoDate);
}

/** End-only places (the grid card): `Ends 22 Nov`, or `null` with no (usable) end date. */
export function dealEndText(deal: MultibuyDeal): string | null {
  const end = dayMonthOf(deal.displayTo);
  return end === null ? null : `Ends ${end}`;
}

/** Range places (the list row): `22 Oct – 22 Nov` with both dates, `Ends 22 Nov` with only the
 *  end, `null` with only the start or neither. */
export function dealDatesText(deal: MultibuyDeal): string | null {
  const end = dayMonthOf(deal.displayTo);
  if (end === null) return null;
  const start = dayMonthOf(deal.displayFrom);
  return start === null ? `Ends ${end}` : `${start} – ${end}`;
}

function joined(parts: readonly (string | null)[]): string {
  return parts.filter((part): part is string => part !== null).join(' · ');
}

/** What the deal offers: its lowest tier's text, or its description for a `DESCRIPTION_ONLY`
 *  deal. */
function offerText(deal: MultibuyDeal): string {
  return deal.kind === 'FREE_UNITS' ? tierText(lowestTier(deal)) : deal.description;
}

/** The compact strip (grid card, mobile list row): `Buy 4, get 1 free · Ends 22 Nov`. */
export function compactDealText(deal: MultibuyDeal): string {
  return joined([offerText(deal), dealEndText(deal)]);
}

/** The wide strip (list row from 768px): `Multi-buy · Buy 4, get 1 free · 22 Oct – 22 Nov`. */
export function wideDealText(deal: MultibuyDeal): string {
  return joined(['Multi-buy', offerText(deal), dealDatesText(deal)]);
}

/** Order deals for the card (interim; the designer owns the final rule). Deals giving free units
 *  come first, by the lowest buy quantity of their lowest tier, then the earliest end date shown,
 *  then the lowest offer code; `DESCRIPTION_ONLY` deals after them, by the earliest end date
 *  shown, then the lowest offer code. A deal with no end date sorts after those with one. */
function compareForCard(left: MultibuyDeal, right: MultibuyDeal): number {
  if (left.kind !== right.kind) return left.kind === 'FREE_UNITS' ? -1 : 1;
  const byBuy =
    left.kind === 'FREE_UNITS' && right.kind === 'FREE_UNITS'
      ? lowestTier(left).buyQuantity - lowestTier(right).buyQuantity
      : 0;
  if (byBuy !== 0) return byBuy;
  const leftEnd = left.displayTo ?? null;
  const rightEnd = right.displayTo ?? null;
  if (leftEnd !== rightEnd) {
    if (leftEnd === null) return 1;
    if (rightEnd === null) return -1;
    return leftEnd < rightEnd ? -1 : 1;
  }
  if (left.multibuyOfferCode === right.multibuyOfferCode) return 0;
  return left.multibuyOfferCode < right.multibuyOfferCode ? -1 : 1;
}

/** The one deal a card shows for a product in several (DQ-4: no "+N more" is drawn), or `null`
 *  when there is none. */
export function cardDeal(deals: readonly MultibuyDeal[]): MultibuyDeal | null {
  return [...deals].sort(compareForCard)[0] ?? null;
}
